// dependencies
import moment from "moment";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { GenerateProcess } from "redux-process";
import React, { useState, useEffect } from "react";

// components
import Button from "Components/Button";

// utils
import get from "Utils/get";

// css
import css from "./CompanySnapshot.styl";

const CompanySnapshot = (props) => {
	const { companyID } = props;

	const [companySnapshot, setSnapshot] = useState(null);
	const [isRefreshing, setIsRefreshing] = useState(null);

	// fetches the company snapshot and loads it into the local state.
	const fetchCompanySnapshot = async () => {
		const request = await props.fetchCompanySnapshot(companyID);

		if (request.succeeded) {
			const { snapshot } = request.response;
			setSnapshot(snapshot);
		} else {
			setSnapshot(null);
		}
	};

	// updates the company snapshot and then refreshes the front-end
	const updateCompanySnapshot = async () => {
		setIsRefreshing(true);

		await props.updateSnapshot(companyID);
		await fetchCompanySnapshot();

		setIsRefreshing(false);
	};

	useEffect(() => {
		fetchCompanySnapshot();
	}, []);

	if (companySnapshot === null) {
		return null;
	}

	const sections = [
		{
			label: "Feature Availability",
			items: [
				{ label: "Workflows", getter: ["features", "workflows"] },
				{ label: "Reviews", getter: ["features", "reviews"] },
				{ label: "Goals", getter: ["features", "goals"] },
				{ label: "Goal Overview and Alignment", getter: ["features", "goal-dashboard"] },
				{ label: "Resources", getter: ["features", "resources"] },
				{ label: "Company Directory", getter: ["features", "directory"] },
				{ label: "Org Chart", getter: ["features", "org-chart"] },
				{ label: "Groups", getter: ["features", "groups"] },
				{ label: "Doc Signing", getter: ["features", "doc-signing"] },
				{ label: "Feedback", getter: ["features", "feedback"] },
				{ label: "SAML", getter: ["features", "saml"] },
				{ label: "IP Whitelisting", getter: ["features", "ip-whitelisting"] },
				{ label: "Enforce Login Methods", getter: ["features", "enforce-login-methods"] },
				{ label: "Recognition Slack Notifications", getter: ["features", "slack-notifications"] },
				{ label: "Engagement", getter: ["features", "engagement"] },
			],
		},
		{
			label: "Authentication and Logins",
			items: [
				{ label: "Active Seats", getter: ["activeSeats"] },
				{ label: "Available Seats", getter: ["availableSeats"] },
				{ label: "Number of Admins", getter: ["adminCount"] },
			],
		},
		{
			label: "Unique users that have logged in",
			items: [
				{ label: "within 30 days", getter: ["userLoginCounts", 30] },
				{ label: "within 90 days", getter: ["userLoginCounts", 90] },
				{ label: "within 180 days", getter: ["userLoginCounts", 180] },
			],
		},
		{
			label: "Unique admins that have logged in",
			items: [
				{ label: "within 30 days", getter: ["adminLoginCounts", 30] },
				{ label: "within 90 days", getter: ["adminLoginCounts", 90] },
				{ label: "within 180 days", getter: ["adminLoginCounts", 180] },
			],
		},
		{
			label: "Unique users that have been created",
			items: [
				{ label: "within 30 days", getter: ["createdUserCounts", 30] },
				{ label: "within 90 days", getter: ["createdUserCounts", 90] },
				{ label: "within 180 days", getter: ["createdUserCounts", 180] },
			],
		},
		{
			label: "Reviews",
			items: [
				{ label: "Number of open review cycles", getter: ["openCyclesCount"] },
				{ label: "Number of open reviews", getter: ["openReviewCount"] },
				{ label: "Number of incomplete review forms", getter: ["openReviewFormsCount"] },
				{ label: "Number of overdue review forms", getter: ["overdueReviewForms"] },
				{
					label: "Average number of forms per review in all non-deleted (last 12 months)",
					getter: ["averageReportCount"],
				},
				{
					label: "% of active users with at least one review started in (last 12 months)",
					getter: ["percentActiveUsersWithReview"],
				},
				{
					label: "Number of forms with last edited dates in last 30 days",
					getter: ["recentlyEditedReviewForms"],
				},
				{
					label: "Number of templates with last edited dates in last 30 days",
					getter: ["recentlyEditedReviewTemplates"],
				},
			],
		},
		{
			label: "Reviews with start dates",
			items: [
				{ label: "within 3 months", getter: ["reviewStartDateCounts", 3] },
				{ label: "within 6 months", getter: ["reviewStartDateCounts", 6] },
				{ label: "within 12 months", getter: ["reviewStartDateCounts", 12] },
			],
		},
		{
			label: "Reviews in a cycle with start dates",
			items: [
				{ label: "within 3 months", getter: ["reviewInCycleStartDateCounts", 3] },
				{ label: "within 6 months", getter: ["reviewInCycleStartDateCounts", 6] },
				{ label: "within 12 months", getter: ["reviewInCycleStartDateCounts", 12] },
			],
		},
		{
			label: "Reviews cycles with start dates",
			items: [
				{ label: "within 3 months", getter: ["reviewCycleStartDateCounts", 3] },
				{ label: "within 6 months", getter: ["reviewCycleStartDateCounts", 6] },
				{ label: "within 12 months", getter: ["reviewCycleStartDateCounts", 12] },
			],
		},
		{
			label: "Goal Admin Settings",
			items: [
				{
					label: "Allow all users to view the Goal Alignment page",
					getter: () => get(["settings", "goal-tree-all-users"], companySnapshot, "disabled"),
				},
				{
					label: "Allow all users to view the Goal Overview page",
					getter: () => get(["settings", "goal-overview-all-users"], companySnapshot, "disabled"),
				},
				{
					label: "Allow all users to view the company goal list page",
					getter: () => get(["settings", "goals-list-all-users"], companySnapshot, "disabled"),
				},
				{
					label: "Allow all users to delete their goals",
					getter: () => get(["settings", "goal-user-delete"], companySnapshot, "disabled"),
				},
				{
					label: "Allow all managers to delete their employee’s goals ",
					getter: () => get(["settings", "goal-manager-delete"], companySnapshot, "disabled"),
				},
				{
					label: "Set goals to private by default",
					getter: () => get(["settings", "default-goals-to-private"], companySnapshot, "disabled"),
				},
				{
					label: "Hide the goals tab for unrelated users",
					getter: () => get(["settings", "hide-goals-profile-page"], companySnapshot, "disabled"),
				},
				{
					label: "Allow goal weighting",
					getter: () => get(["settings", "goal-weights"], companySnapshot, "disabled"),
				},
			],
		},
		{
			label: "Goals",
			items: [
				{ label: "Number of active goals", getter: ["activeGoals"] },
				{ label: "Number of overdue active goals", getter: ["overdueActiveGoals"] },
				{ label: "Number of private active goals", getter: ["activePrivateGoals"] },
				{ label: "Number of active top goals", getter: ["activeTopGoals"] },
				{ label: "Number of aligned goals", getter: ["alignedGoals"] },
				{ label: "Average number of goals per active user", getter: ["averageGoalsPerActiveUser"] },
				{ label: "Median number of goals per active user", getter: ["medianGoalsPerActiveUser"] },
				{ label: "Average % completion of active goals", getter: ["goalsAverageProgress"] },
				{ label: "Median % completion of active goals", getter: ["goalsMedianProgress"] },
				{
					label: "Percent of employees with at least one active goal",
					getter: ["percentOfEmployeesWithAGoal"],
				},
				{ label: "Total number of updates to goals in last 12 months", getter: ["goalsTotalUpdates12Months"] },
				{
					label: "Total number of goals that have been updated in last 12 months",
					getter: ["goalsUpdated12Months"],
				},
				{
					label: "Average number of updates per goal in last 12 months",
					getter: () => get(["averageUpatesPerGoal12Months"], companySnapshot, 0).toFixed(1),
				},
				{
					label: "Median number of updates per goal in last 12 months",
					getter: ["medianUpatesPerGoal12Months"],
				},
				{ label: "Most updates to a single goal in last 12 months", getter: ["goalMostUpdatedCount"] },
			],
		},
		{
			label: "Goals that have been created",
			items: [
				{ label: "within 3 months", getter: ["goalsCreated", 3] },
				{ label: "within 6 months", getter: ["goalsCreated", 6] },
				{ label: "within 12 months", getter: ["goalsCreated", 12] },
			],
		},
		{
			label: "Goals that have been completed",
			items: [
				{ label: "within 3 months", getter: ["goalsCompleted", 3] },
				{ label: "within 6 months", getter: ["goalsCompleted", 6] },
				{ label: "within 12 months", getter: ["goalsCompleted", 12] },
			],
		},
		{
			label: "Feedback",
			items: [
				{ label: "% of employees who have given feedback", getter: ["percentActiveUsersWhoHaveGivenFeedback"] },
				{
					label: "% of employees who have received feedback",
					getter: ["percentActiveUsersWhoHaveReceivedFeedback"],
				},
			],
		},
		{
			label: "Private notes created",
			items: [
				{ label: "within 3 months", getter: ["feedbackPrivateNotes", 3] },
				{ label: "within 6 months", getter: ["feedbackPrivateNotes", 6] },
				{ label: "within 12 months", getter: ["feedbackPrivateNotes", 12] },
			],
		},
		{
			label: "Shared feedback created",
			items: [
				{ label: "within 3 months", getter: ["feedbackShared", 3] },
				{ label: "within 6 months", getter: ["feedbackShared", 6] },
				{ label: "within 12 months", getter: ["feedbackShared", 12] },
			],
		},
		{
			label: "Recognition created",
			items: [
				{ label: "within 3 months", getter: ["feedbackRecognition", 3] },
				{ label: "within 6 months", getter: ["feedbackRecognition", 6] },
				{ label: "within 12 months", getter: ["feedbackRecognition", 12] },
			],
		},
		{
			label: "Workflows and Signatures",
			items: [
				{ label: "Number of open workflows", getter: ["workflowsOpen"] },
				{
					label: "Average number of todos per workflow in the last 12 months",
					getter: ["workflowsAverageTodosCount"],
				},
				{ label: "Number of open todos", getter: ["workflowsOpenTodos"] },
				{
					label: "% of active users with at least one workflow started in the last 12 months",
					getter: ["percentActiveUsersWithAWorkflow"],
				},
				{
					label: "Number of workflow templates edited in the last 30 days",
					getter: ["workflowTemplatesRecentlyEdited"],
				},
			],
		},
		{
			label: "Number of workflows with start dates",
			items: [
				{ label: "within 3 months", getter: ["workflowsStarted", 3] },
				{ label: "within 6 months", getter: ["workflowsStarted", 6] },
				{ label: "within 12 months", getter: ["workflowsStarted", 12] },
			],
		},
		{
			label: "Number of signature requests todos with start dates",
			items: [
				{ label: "within 3 months", getter: ["workflowsSignatures", 3] },
				{ label: "within 6 months", getter: ["workflowsSignatures", 6] },
				{ label: "within 12 months", getter: ["workflowsSignatures", 12] },
			],
		},
		{
			label: "Storage",
			items: [
				{ label: "Storage used", getter: ["storageUsedRounded"] },
				{ label: "Storage available", getter: ["storageAvailable"] },
				{ label: "Average storage used per user", getter: ["averageStorageUsedPerUser"] },
			],
		},
		{
			label: "Resources",
			items: [
				{ label: "Total number of company documents", getter: ["companyDocumentsCount"] },
				{ label: "Total number of company links", getter: ["companyLinksCount"] },
			],
		},
		{
			label: "Reporting",
			items: [{ label: "Total number of custom field reports", getter: ["customFieldReportsCount"] }],
		},
		{
			label: "Data Fields",
			items: [{ label: "Total number of data fields", getter: ["dataFieldsCount"] }],
		},
		{
			label: "Groups",
			items: [
				{ label: "Total number of groups", getter: ["totalGroups"] },
				{
					label: "Total number of groups created in the last 12 months",
					getter: ["totalGroupsCreatedInLast12Months"],
				},
				{ label: "Average number of employees per group", getter: ["averageEmployeesPerGroup"] },
				{ label: "Average number of admins per group", getter: ["averageAdminsPerGroup"] },
			],
		},
		{
			label: "Engagement",
			items: [
				{ label: "Module Status", getter: ["features", "engagement"] },
				{ label: "Survey Status", getter: ["engagementEnabled"] },
				{ label: "Survey Frequency", getter: ["surveyFrequency"] },
				{ label: "Current Survey Start Date", getter: ["currentSurveyStartDate"] },
				{ label: "Next Survey Start Date", getter: ["nextSurveyStartDate"] },
				{ label: "Participation Rate (Current Survey Period)", getter: ["currentParticipationRate"] },
				{ label: "Participation Rate (Previous Survey Period)", getter: ["previousParticipationRate"] },
				{ label: "Dynamic Cohort Count", getter: ["dynamicCohortCount"] },
				{ label: "Static Cohort Count", getter: ["staticCohortCount"] },
			],
		},
		{
			label: "Meetings",
			items: [
				{ label: "Module Status", getter: ["meetingsModuleStatus"] },
				{ label: "Number of Meeting Workspaces", getter: ["meetingWorkspacesCount"] },
				{ label: "Number of Active Meeting Workspaces", getter: ["activeMeetingWorkspacesCount"] },
				{ label: "Number of Meetings", getter: ["meetingsCount"] },
				{
					label: "Users participating in at least one meeting",
					getter: ["percentUsersParticipatingInAtLeastOneMeeting"],
				},
			],
		},
	];

	if (get(["features", "enforce-login-methods"], companySnapshot, null) === "enabled") {
		sections.splice(1, 0, {
			label: "Authentication Methods",
			items: Object.keys(get(["authenticationSettings", "authentication_methods"], companySnapshot, {})).map(
				(key) => {
					const label = key;
					const getter = ["authenticationSettings", "authentication_methods", key, "state"];

					return {
						label,
						getter,
					};
				}
			),
		});
	}

	if (
		get(["features", "enforce-login-methods"], companySnapshot, null) === "enabled" &&
		"authenticationSettingsV2" in companySnapshot
	) {
		sections.splice(1, 1, {
			label: "Authentication Methods",
			items: companySnapshot.authenticationSettingsV2.map((method) => {
				const label = method.type === "saml" ? method.label : method.type;
				const getter = () => method.state;

				return {
					label,
					getter,
				};
			}),
		});
	}

	return (
		<div className={css.main}>
			<div className={css.snapShotTime}>
				Snapshot from {moment(companySnapshot.createdAt).calendar()}
				<div className={css.updateSnapshotWrapper}>
					<Button
						color="black"
						isRequesting={isRefreshing}
						spinnerSize={16}
						onClick={updateCompanySnapshot}
						className={css.updateSnapshot}
					>
						Refresh
					</Button>
				</div>
			</div>

			<div className={css.snapshotContent}>
				<div>
					{sections.map((section) => {
						const { label } = section;

						return (
							<div className={css.sectionBox} key={label}>
								<div className={css.sectionLabel}>{label}</div>
								<div>
									{section.items.map((item) => {
										const itemLabel = item.label;
										const value =
											typeof item.getter === "function"
												? item.getter()
												: get(item.getter, companySnapshot, "");

										return (
											<div className={css.dataBox} key={itemLabel}>
												<div className={css.dataLabel}>{itemLabel}</div>
												<div className={css.dataValue}>{value}</div>
											</div>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
	fetchCompanySnapshot: (companyID) =>
		dispatch(GenerateProcess("GetCompanySnapshot", { companyID, __ignoreError: true })),
	updateSnapshot: (companyID) => dispatch(GenerateProcess("UpdateCompanySnapshot", { companyID })),
});

CompanySnapshot.propTypes = {
	updateSnapshot: PropTypes.func.isRequired,
	fetchCompanySnapshot: PropTypes.func.isRequired,
	companyID: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanySnapshot);
